import { Injectable } from '@angular/core';
import { IndividualConfig, ToastrService } from 'ngx-toastr';
import { Component } from '@angular/core';
import { ToastRef } from 'ngx-toastr';
import { firstValueFrom, Subject } from 'rxjs';
import { IConfirmDialog } from '../shared/interfaces/IDialog';
import { MatDialog } from '@angular/material/dialog';
import { AppConfirmDialogComponent } from '../shared/components/confirm-dialog/confirm-dialog.component';

@Injectable({
  providedIn: 'root'
})
export class ToastService {
  defaultConfig: Partial<IndividualConfig> = {
    enableHtml: true,
    timeOut: 3000, // How long the toast will be displayed (milliseconds)
    positionClass: 'toast-top-right', // Position on the screen
    newestOnTop: true, // Display newest toasts on top
    tapToDismiss: true, // Dismiss toast on tap/click
    easeTime: 300 // Animation speed,
  };

  constructor(
    private toastr: ToastrService,
    private dialog: MatDialog,
  ) { }

  success(message = '', title = '', config = {}) {
    this.toastr.success(message, title, { ...this.defaultConfig, ...config, toastClass: 'toast toast-success' });
  }

  error(message = '', title = '', config = {}) {
    this.toastr.error(message, title, { ...this.defaultConfig, ...config, toastClass: 'toast toast-error' });
  }

  warning(message = '', title = '', config = {}) {
    this.toastr.warning(message, title, { ...this.defaultConfig, ...config, toastClass: 'toast toast-warning' });
  }

  info(message = '', title = '', config = {}) {
    this.toastr.info(message, title, { ...this.defaultConfig, ...config, toastClass: 'toast toast-info' });
  }

  async confirm(message: string, title = 'Confirm'): Promise<boolean> {
    const toastRef = this.toastr.show('', '', {
      ...this.defaultConfig,
      toastComponent: ToastConfirmComponent,
      toastClass: 'toast toast-confirm'
    });

    const instance = toastRef.toastRef.componentInstance as ToastConfirmComponent;
    instance.title = title;
    instance.message = message;

    const result = await firstValueFrom(instance.confirm$);
    return result;
  }

  confirmDelete(config: IConfirmDialog) {
    const dialogRef = this.dialog.open(AppConfirmDialogComponent, {
      data: config,
      width: config.width,
      maxWidth: config.maxWidth
    });
    dialogRef.afterClosed().subscribe((confirm) => {
      confirm ? config.onConfirm() : config.onCancel?.();
    });
  }
}

@Component({
  selector: 'app-toast-confirm',
  template: `
    <div class="p-3">
      <div class="font-medium mb-2">{{ title }}</div>
      <div class="text-sm mb-3">{{ message }}</div>
      <div class="flex justify-end gap-2">
        <button class="px-3 py-1 bg-green-500 text-white rounded" (click)="confirm(true)">Yes</button>
        <button class="px-3 py-1 bg-gray-300 text-gray-800 rounded" (click)="confirm(false)">No</button>
      </div>
    </div>
  `
})
export class ToastConfirmComponent {
  title = '';
  message = '';

  private _confirm$ = new Subject<boolean>();
  confirm$ = this._confirm$.asObservable();

  constructor(private toastRef: ToastRef<any>) { }

  confirm(result: boolean) {
    this._confirm$.next(result);
    this._confirm$.complete();
    this.toastRef.close();
  }
}
